import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, Coins, Sparkles, X, Plus, Minus, Crown, BookOpen } from 'lucide-react';
import { API } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { MobileNav } from '../components/MobileNav';
import { WinnerFeed } from '../components/WinnerFeed';

const playfairFont = "'Playfair Display', serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);

const TIER_COLORS = {
  gold: '#D4AF37',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  common: '#A3B8AC',
  last: '#EF4444',
};
const TIER_LABEL = {
  gold: 'GOLD', silver: 'SILVER', bronze: 'BRONZE', common: 'COMMON', last: 'LAST PRIZE',
};

const QUICK_COUNTS = [1, 5, 10, 20, 50, 100];
const MIN_COUNT = 1;
const MAX_COUNT = 100;

const FALLBACK_BG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><defs><linearGradient id="g" x1="0%25" y1="0%25" x2="100%25" y2="100%25"><stop offset="0%25" stop-color="%23032014"/><stop offset="100%25" stop-color="%23080C0A"/></linearGradient></defs><rect fill="url(%23g)" width="800" height="400"/><text x="400" y="200" fill="%23D4AF37" font-family="serif" font-size="32" text-anchor="middle" dy=".3em" font-style="italic">Kuji</text></svg>';

const FALLBACK_PRIZE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect fill="%23202824" width="200" height="200"/><text x="100" y="100" fill="%23A3B8AC" font-family="sans-serif" font-size="13" text-anchor="middle" dy=".3em">No image</text></svg>';

export const KujiDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(1);
  const [drawing, setDrawing] = useState(false);
  const [revealResult, setRevealResult] = useState(null);
  const [showDrawConfirm, setShowDrawConfirm] = useState(false);

  const fetchEvent = useCallback(async () => {
    try {
      setLoading(true);
      const list = await axios.get(`${API}/kuji/events`);
      const match = (list.data || []).find((e) => e.slug === slug);
      if (!match) {
        setEvent(null);
        return;
      }
      const detail = await axios.get(`${API}/kuji/events/${match.id}`);
      setEvent(detail.data);
    } catch (_e) {
      setEvent(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  const handleDraw = async () => {
    if (!event || drawing) return;
    if (event.status !== 'active') {
      toast.error('Event is not active');
      return;
    }
    if (count > event.remaining_slots) {
      toast.error(`Only ${event.remaining_slots} slots remaining`);
      return;
    }
    const cost = count * event.coin_price_per_draw;
    if ((user?.coin_balance || 0) < cost) {
      toast.error('Insufficient coins. Please top up.');
      return;
    }
    // Open confirm modal (replaces window.confirm)
    setShowDrawConfirm(true);
  };

  const performDraw = async () => {
    setShowDrawConfirm(false);
    setDrawing(true);
    try {
      const idemKey = `${event.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const { data } = await axios.post(
        `${API}/kuji/events/${event.id}/draw`,
        { count },
        { withCredentials: true, headers: { 'Idempotency-Key': idemKey } }
      );
      setRevealResult(data);
      await checkAuth();
      await fetchEvent();

      // Notifikasi: hadiah fisik masuk Inventori
      const physicalCount = (data?.draws || []).filter((d) => d.is_physical).length;
      if (physicalCount > 0) {
        toast.success(
          `🎁 ${physicalCount} hadiah baru masuk Inventori — klik untuk klaim`,
          {
            duration: 8000,
            action: {
              label: 'Buka Inventori',
              onClick: () => navigate('/inventory'),
            },
          }
        );
        // Trigger global refresh so the bottom-nav badge updates immediately
        window.dispatchEvent(new CustomEvent('inventory-updated'));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Draw failed');
    } finally {
      setDrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A' }}>
        <div className="inline-block w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A', color: '#A3B8AC' }}>
        <div className="text-center">
          <p className="mb-4">Event not found.</p>
          <button onClick={() => navigate('/kuji')} className="px-5 py-2 rounded-full" style={{ background: '#D4AF37', color: '#080C0A' }}>Back to Kuji</button>
        </div>
      </div>
    );
  }

  const remaining = event.remaining_slots || 0;
  const total = event.total_slots || 1;
  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  const cost = count * event.coin_price_per_draw;
  const isActive = event.status === 'active';

  return (
    <div className="min-h-screen pb-56" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }} data-testid="kuji-detail-page">
      {/* Banner */}
      <div
        className="relative h-64 md:h-80"
        style={{
          backgroundImage: `url(${event.banner_url || event.cover_url || FALLBACK_BG})`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,12,10,0.3) 0%, rgba(8,12,10,0.95) 100%)' }} />
        <header className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <button onClick={() => navigate('/kuji')} className="flex items-center gap-2 backdrop-blur-md px-3 py-2 rounded-full" style={{ background: 'rgba(0,0,0,0.4)' }} data-testid="kuji-detail-back">
            <ArrowLeft className="w-4 h-4" style={{ color: '#F8FAF9' }} />
            <span className="text-sm" style={{ color: '#F8FAF9' }}>Back</span>
          </button>
          <div className="backdrop-blur-md px-3 py-2 rounded-full flex items-center gap-2" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Coins className="w-4 h-4" style={{ color: '#D4AF37' }} />
            <span className="text-sm font-semibold" style={{ color: '#D4AF37' }} data-testid="kuji-coin-balance">{fmt(user?.coin_balance)}</span>
            <span className="text-[10px]" style={{ color: '#A3B8AC' }}>(≈ Rp{fmt((user?.coin_balance || 0) * 1000)})</span>
          </div>
        </header>
        <div className="absolute bottom-6 left-0 right-0 max-w-7xl mx-auto px-6 lg:px-12">
          <h1 className="text-3xl md:text-5xl font-light mb-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>{event.name}</h1>
          <p className="text-sm md:text-base max-w-2xl" style={{ color: '#A3B8AC' }}>{event.description}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-8">
        {/* Realtime stock callout */}
        <div className="mb-6 rounded-2xl p-5 backdrop-blur-xl flex items-center justify-between gap-4 flex-wrap" style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(34,197,94,0.04))', border: '1px solid rgba(212,175,55,0.2)' }} data-testid="kuji-stock-callout">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#D4AF37' }}>Realtime Stock</p>
            <p className="text-3xl font-light mt-1" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
              Sisa <span style={{ color: remaining > 0 ? '#22C55E' : '#EF4444' }} data-testid="kuji-stock-remaining">{fmt(remaining)}</span>
              <span className="text-base opacity-60"> dari {fmt(total)}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#A3B8AC' }}>Status</p>
            <span className="inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: isActive ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)', color: isActive ? '#22C55E' : '#EAB308' }}>
              {event.status.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat label="Cost / draw" value={`${fmt(event.coin_price_per_draw)}`} color="#D4AF37" suffix={`coin (≈ Rp${fmt(event.coin_price_per_draw * 1000)})`} />
          <Stat label="Slots left" value={fmt(remaining)} color={remaining > 0 ? '#22C55E' : '#EF4444'} suffix={`/ ${fmt(total)}`} />
          <Stat label="Prizes" value={fmt((event.prizes || []).length)} color="#F8FAF9" />
          <Stat label="Your coins" value={fmt(user?.coin_balance)} color="#D4AF37" suffix={`(≈ Rp${fmt((user?.coin_balance || 0) * 1000)})`} />
        </div>

        {/* Slot progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs mb-1.5" style={{ color: '#A3B8AC' }}>
            <span>Remaining slots</span>
            <span>{pct.toFixed(1)}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #D4AF37, #F0C955)' }} data-testid="kuji-progress-bar" />
          </div>
        </div>

        {/* Prize gallery grouped by tier — last-tier prize separated below */}
        <h2 className="text-2xl font-light mb-4" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Prize Pool</h2>
        {(() => {
          const nonLast = (event.prizes || []).filter((p) => p.tier !== 'last');
          const tierOrder = ['gold', 'silver', 'bronze', 'common'];
          const grouped = {};
          for (const p of nonLast) {
            const t = p.tier || 'common';
            if (!grouped[t]) grouped[t] = [];
            grouped[t].push(p);
          }
          return (
            <div className="space-y-6 mb-8" data-testid="kuji-prize-gallery">
              {tierOrder.filter((t) => grouped[t]?.length).map((t) => (
                <div key={t}>
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4" style={{ color: TIER_COLORS[t] }} />
                    <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: TIER_COLORS[t] }}>
                      {TIER_LABEL[t]} · {grouped[t].length} prize{grouped[t].length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {grouped[t].map((p) => {
                      const rem = typeof p.remaining_quantity === 'number' ? p.remaining_quantity : p.quantity;
                      const totalQ = p.quantity || 1;
                      const pct = Math.max(0, Math.min(100, (rem / totalQ) * 100));
                      const soldOut = rem === 0;
                      return (
                        <div
                          key={p.id}
                          className="rounded-xl overflow-hidden backdrop-blur-xl"
                          style={{
                            background: 'rgba(255,255,255,0.03)',
                            border: `1px solid ${TIER_COLORS[t]}55`,
                            opacity: soldOut ? 0.45 : 1,
                          }}
                          data-testid={`kuji-prize-${p.id}`}
                        >
                          <div className="relative">
                            <img
                              src={p.image_url || FALLBACK_PRIZE}
                              alt={p.name}
                              className="w-full aspect-square object-cover"
                              onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_PRIZE; }}
                            />
                            {soldOut && (
                              <span className="absolute top-2 right-2 px-2 py-1 rounded-full text-[10px] font-bold" style={{ background: 'rgba(0,0,0,0.7)', color: '#EF4444' }}>
                                Habis
                              </span>
                            )}
                          </div>
                          <div className="p-3">
                            <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{p.name}</p>
                            <p className="text-[11px] mt-1" style={{ color: '#A3B8AC' }}>
                              Sisa <span style={{ color: rem > 0 ? '#22C55E' : '#EF4444' }}>{fmt(rem)}</span> / {fmt(totalQ)}
                              {typeof p.probability === 'number' && (
                                <> · {(p.probability * 100).toFixed(2)}%</>
                              )}
                            </p>
                            <div className="h-1 mt-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: TIER_COLORS[t] }} />
                            </div>
                            {p.is_physical === false && p.digital_reward_coins > 0 && (
                              <p className="text-xs mt-1.5" style={{ color: '#D4AF37' }}>+{fmt(p.digital_reward_coins)} coins</p>
                            )}
                            {p.buyback_coins > 0 && (
                              <p className="text-xs mt-0.5" style={{ color: '#A3B8AC' }}>
                                Jual kembali: <span style={{ color: '#D4AF37' }}>{fmt(p.buyback_coins)} coins</span>
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Last Prize — dedicated section */}
        {(event.prizes || []).filter((p) => p.tier === 'last').map((p) => (
          <div key={p.id} className="rounded-2xl overflow-hidden mb-8 p-5 md:p-6 flex flex-col md:flex-row gap-5 items-center" style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.12), rgba(212,175,55,0.06))', border: '1.5px solid rgba(239,68,68,0.4)' }} data-testid="kuji-last-prize-section">
            <img
              src={p.image_url || FALLBACK_PRIZE}
              alt={p.name}
              className="w-32 h-32 md:w-44 md:h-44 rounded-xl object-cover flex-shrink-0"
              style={{ border: '2px solid rgba(239,68,68,0.5)' }}
              onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_PRIZE; }}
            />
            <div className="flex-1 min-w-0 text-center md:text-left">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-2" style={{ background: 'rgba(239,68,68,0.2)' }}>
                <Crown className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                <span className="text-[10px] tracking-[0.2em] font-bold" style={{ color: '#EF4444' }}>LAST PRIZE</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-light mb-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>{p.name}</h3>
              <p className="text-sm mb-2" style={{ color: '#A3B8AC' }}>
                Hadiah eksklusif untuk slot terakhir. Akan diberikan otomatis kepada penarik yang mendapat slot final.
              </p>
              {p.description && (
                <p className="text-xs italic" style={{ color: '#A3B8AC' }}>{p.description}</p>
              )}
            </div>
          </div>
        ))}

        <p className="text-xs text-center mb-4 italic" style={{ color: '#A3B8AC' }}>
          Outcomes are determined by a pre-generated slot pool. Every draw is final and audited.
        </p>

        {/* Realtime Winner Feed — filtered to this event */}
        <div className="mb-8">
          <WinnerFeed variant="inline" eventId={event.id} limit={30} />
        </div>
      </div>

      {/* Sticky draw bar — positioned above MobileNav (which is fixed bottom-0 height ~64px) */}
      <div className="fixed bottom-[68px] md:bottom-[68px] left-0 right-0 z-[55] backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.92)', borderTop: '1px solid rgba(212,175,55,0.2)' }} data-testid="kuji-draw-bar">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-3">
          <div className="flex items-center gap-2 mb-2 overflow-x-auto hide-scrollbar">
            {QUICK_COUNTS.map((n) => {
              const active = count === n;
              const disabled = n > remaining;
              return (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  disabled={disabled || !isActive}
                  className="flex-1 min-w-[58px] py-2 rounded-full text-xs font-semibold whitespace-nowrap disabled:opacity-30"
                  style={{
                    background: active ? '#D4AF37' : 'rgba(255,255,255,0.05)',
                    color: active ? '#080C0A' : '#F8FAF9',
                    border: `1px solid ${active ? '#D4AF37' : 'rgba(255,255,255,0.1)'}`
                  }}
                  data-testid={`kuji-count-${n}x`}
                >
                  {n}×
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-wider hidden sm:inline" style={{ color: '#A3B8AC' }}>Qty</span>
            <button
              type="button"
              onClick={() => setCount((c) => Math.max(MIN_COUNT, c - 1))}
              disabled={count <= MIN_COUNT}
              className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30 flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)' }}
              data-testid="kuji-count-minus"
            >
              <Minus className="w-4 h-4" style={{ color: '#F8FAF9' }} />
            </button>
            <input
              type="number"
              min={MIN_COUNT}
              max={MAX_COUNT}
              value={count}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (isNaN(v)) return;
                setCount(Math.max(MIN_COUNT, Math.min(MAX_COUNT, v)));
              }}
              className="flex-1 rounded-full px-3 py-2 text-center text-base font-semibold outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' }}
              data-testid="kuji-count-custom-input"
            />
            <button
              type="button"
              onClick={() => setCount((c) => Math.min(MAX_COUNT, Math.min(remaining || MAX_COUNT, c + 1)))}
              disabled={count >= Math.min(MAX_COUNT, remaining || MAX_COUNT)}
              className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30 flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)' }}
              data-testid="kuji-count-plus"
            >
              <Plus className="w-4 h-4" style={{ color: '#F8FAF9' }} />
            </button>
            <span className="text-[10px]" style={{ color: '#A3B8AC' }}>1–{Math.min(MAX_COUNT, remaining || MAX_COUNT)}</span>
          </div>
          <button
            onClick={handleDraw}
            disabled={drawing || !isActive || remaining === 0 || count < MIN_COUNT || count > MAX_COUNT || count > remaining}
            className="w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: '#D4AF37', color: '#080C0A' }}
            data-testid="kuji-draw-btn"
          >
            {drawing ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#080C0A', borderTopColor: 'transparent' }} />
                Drawing...
              </>
            ) : !isActive ? (
              <>Event {event.status}</>
            ) : remaining === 0 ? (
              <>Sold Out</>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Draw ×{count} · {fmt(cost)} coins
              </>
            )}
          </button>
        </div>
      </div>

      {/* Draw confirmation modal (replaces window.confirm + links to T&C) */}
      <AnimatePresence>
        {showDrawConfirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6"
            style={{ background: 'rgba(8,12,10,0.95)' }}
            onClick={() => setShowDrawConfirm(false)}
            data-testid="draw-confirm-modal"
          >
            <motion.div
              initial={{ scale: 0.96, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }}
              className="rounded-2xl p-6 max-w-md w-full backdrop-blur-xl space-y-4"
              style={{ background: '#0c1f17', border: '1px solid rgba(212,175,55,0.4)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <h3 className="text-lg font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Konfirmasi Draw</h3>
              </div>
              <p className="text-sm" style={{ color: '#CFE0D5' }}>Anda akan melakukan draw:</p>
              <div className="rounded-xl p-3 space-y-1.5 text-sm" style={{ background: 'rgba(0,0,0,0.3)' }}>
                <div className="flex justify-between"><span style={{ color: '#A3B8AC' }}>Jumlah</span><span style={{ color: '#F8FAF9' }} data-testid="draw-confirm-count">x{count}</span></div>
                <div className="flex justify-between"><span style={{ color: '#A3B8AC' }}>Biaya</span><span style={{ color: '#D4AF37' }} data-testid="draw-confirm-cost">{fmt(cost)} Coin (≈ Rp{fmt(cost * 1000)})</span></div>
                <div className="flex justify-between"><span style={{ color: '#A3B8AC' }}>Event</span><span className="text-right truncate ml-2" style={{ color: '#F8FAF9' }}>{event.name}</span></div>
              </div>
              <p className="text-xs" style={{ color: '#A3B8AC' }}>
                Dengan melanjutkan, Anda setuju dengan{' '}
                <a
                  href="/kuji-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: '#D4AF37' }}
                  data-testid="draw-confirm-terms-link"
                >
                  Syarat & Ketentuan Kuji
                </a>.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowDrawConfirm(false)}
                  className="px-5 py-2 rounded-full text-sm"
                  style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }}
                  data-testid="draw-confirm-cancel-btn"
                >
                  Batal
                </button>
                <button
                  onClick={performDraw}
                  className="px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-2"
                  style={{ background: '#D4AF37', color: '#080C0A' }}
                  data-testid="draw-confirm-accept-btn"
                >
                  <Sparkles className="w-4 h-4" /> Setuju & Draw
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result reveal modal */}
      <AnimatePresence>
        {revealResult && (
          <RevealModal
            result={revealResult}
            onClose={() => setRevealResult(null)}
          />
        )}
      </AnimatePresence>

      <MobileNav />
    </div>
  );
};

const Stat = ({ label, value, color, suffix }) => (
  <div className="rounded-xl p-3 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
    <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: '#A3B8AC' }}>{label}</p>
    <p className="text-lg font-semibold" style={{ color, fontFamily: playfairFont }}>
      {value}{suffix && <span className="text-xs ml-1 opacity-70">{suffix}</span>}
    </p>
  </div>
);

/**
 * Cosmetic reveal of draw results. Animation is purely visual — does not
 * affect or compute outcomes (server already determined them via slot pool).
 */
const RevealModal = ({ result, onClose }) => {
  const draws = result.draws || [];
  const refunded = result.refunded_count || 0;
  const [step, setStep] = useState(0); // index of card currently revealed

  useEffect(() => {
    if (step >= draws.length) return;
    const t = setTimeout(() => setStep((s) => s + 1), 350);
    return () => clearTimeout(t);
  }, [step, draws.length]);

  const allRevealed = step >= draws.length;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      data-testid="kuji-reveal-modal"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 md:p-8"
        style={{ background: '#080C0A', border: '1px solid rgba(212,175,55,0.3)' }}
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
              {draws.length === 1 ? 'Your Prize' : `Your ${draws.length} Prizes`}
            </h2>
            {refunded > 0 && (
              <p className="text-sm mt-1" style={{ color: '#EAB308' }}>
                {refunded} draw{refunded > 1 ? 's' : ''} refunded (sold out)
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }} data-testid="kuji-reveal-close">
            <X className="w-5 h-5" style={{ color: '#F8FAF9' }} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {draws.map((d, i) => (
            <RevealCard key={d.id || i} draw={d} revealed={i < step} />
          ))}
        </div>

        {allRevealed && (
          <div className="mt-6 flex flex-col md:flex-row gap-3 items-center">
            <button onClick={onClose} className="w-full md:w-auto px-6 py-2.5 rounded-full text-sm font-semibold" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }} data-testid="kuji-reveal-done">
              Done
            </button>
            <p className="text-xs flex-1 text-center md:text-left" style={{ color: '#A3B8AC' }}>
              Physical prizes are stored in your inventory. Digital rewards have been credited.
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const RevealCard = ({ draw, revealed }) => {
  const tierColor = TIER_COLORS[draw.prize_tier_snapshot] || '#F8FAF9';
  const isLast = !!draw.is_last_prize;
  return (
    <motion.div
      initial={{ rotateY: 180, opacity: 0 }}
      animate={{ rotateY: revealed ? 0 : 180, opacity: revealed ? 1 : 0.3 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: isLast ? '2px solid #EF4444' : `1px solid ${tierColor}55`,
        boxShadow: isLast && revealed ? '0 0 24px rgba(239,68,68,0.4)' : 'none',
      }}
    >
      {revealed ? (
        <>
          <img
            src={draw.prize_image_snapshot || FALLBACK_PRIZE}
            alt={draw.prize_name_snapshot}
            className="w-full aspect-square object-cover"
            onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_PRIZE; }}
          />
          <div className="p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Trophy className="w-3 h-3" style={{ color: tierColor }} />
              <span className="text-[10px] font-semibold tracking-wider" style={{ color: tierColor }}>
                {(TIER_LABEL[draw.prize_tier_snapshot] || draw.prize_tier_snapshot)?.toUpperCase()}
              </span>
              {isLast && <span className="ml-auto text-[10px] font-bold" style={{ color: '#EF4444' }}>★</span>}
            </div>
            <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{draw.prize_name_snapshot}</p>
            {draw.is_physical === false && draw.digital_reward_coins > 0 && (
              <p className="text-xs mt-0.5" style={{ color: '#D4AF37' }}>+{fmt(draw.digital_reward_coins)} coins</p>
            )}
          </div>
        </>
      ) : (
        <div className="aspect-square flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #032014, #080C0A)' }}>
          <Sparkles className="w-10 h-10" style={{ color: '#D4AF37' }} />
        </div>
      )}
    </motion.div>
  );
};
